"use client"

import type { OppResolvedParty, PooClientProjection } from '@/contracts/poo'
import { treeStore } from '@/msgdeskRuntime/state'
import { valueRepresentations } from '@/app/richEdit/serialize/valueRepresentations'

interface PooApiResult {
  ok: boolean
  protocol?: PooClientProjection
  error?: string
  resolution?: OppRecipientResolution
  enrollment?: OppEnrollmentStatus
}

export type OppRecipientResolution =
  | { status: 'ready'; party: OppResolvedParty; senderExternalEmailReady: boolean }
  | { status: 'needs-enrollment'; normalizedEmail: string }
  | { status: 'not-found' | 'ambiguous' | 'unusable' }

export interface OppEnrollmentStatus {
  state: 'none' | 'pending' | 'completed' | 'cancelled' | 'expired'
  deliveryState: 'none' | 'pending' | 'accepted' | 'failed'
  deliveryError: string
  expiresAt: number | null
  recipientLookup: string
  recipient: OppResolvedParty | null
}

const KDF_ITERATIONS = 310_000
const pending = new Map<string, Promise<PooClientProjection>>()

function canonical(value: unknown): string {
  const normalize = (item: unknown): unknown => {
    if (typeof item === 'string') return item.normalize('NFC')
    if (Array.isArray(item)) return item.map(normalize)
    if (item && typeof item === 'object') {
      return Object.fromEntries(
        Object.entries(item as Record<string, unknown>)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([key, child]) => [key.normalize('NFC'), normalize(child)]),
      )
    }
    return item
  }
  return JSON.stringify(normalize(value))
}

async function sha256Hex(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value))
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('')
}

export function generateOppMasterCode(): string {
  const bytes = new Uint8Array(32)
  crypto.getRandomValues(bytes)
  return valueRepresentations.packBytesUrl(bytes)
}

function validMasterCode(value: string): string {
  const normalized = value.normalize('NFC')
  if ([...normalized].length < 20) throw new Error('Use the generated high-entropy code or a long code stored by your password manager')
  if ([...normalized].length > 512) throw new Error('The code is too long')
  return normalized
}

/** New registrations never commit ambiguous surrounding whitespace. Existing
 * documents remain byte-compatible: their Open/Accept path checks the exact
 * entered value before considering a trimmed paste fallback. */
export function normalizeOppRegistrationMasterCode(value: string): string {
  return validMasterCode(value.trim())
}

async function deriveReveal(
  protocol: PooClientProjection,
  masterCode: string,
  purpose: 'open' | 'accept',
): Promise<string> {
  const code = validMasterCode(masterCode)
  const encoder = new TextEncoder()
  const key = await crypto.subtle.importKey('raw', encoder.encode(code), 'PBKDF2', false, ['deriveBits'])
  const salt = encoder.encode(canonical([
    protocol.protocolVersion === 23 ? 'OPP/KDF/v2.3' : 'OPP/KDF/v2.2',
    protocol.messageUuid,
    purpose,
  ]))
  const bits = await crypto.subtle.deriveBits({
    name: 'PBKDF2',
    hash: 'SHA-256',
    salt,
    iterations: KDF_ITERATIONS,
  }, key, 256)
  return valueRepresentations.packBytesUrl(new Uint8Array(bits))
}

async function commitment(
  protocol: PooClientProjection,
  purpose: 'open' | 'accept',
  reveal: string,
): Promise<string> {
  if (!protocol.recipientId) throw new Error('The OPP recipient is not configured')
  return sha256Hex(canonical([
    protocol.protocolVersion === 23
      ? purpose === 'open' ? 'OPP/OPEN_COMMIT/v2.3' : 'OPP/ACCEPT_COMMIT/v2.3'
      : purpose === 'open' ? 'OPP/OPEN_COMMIT/v2.2' : 'OPP/ACCEPT_COMMIT/v2.2',
    protocol.messageUuid,
    ...(protocol.protocolVersion === 23 ? [protocol.offerHash ?? ''] : []),
    protocol.recipientId,
    reveal,
  ]))
}

async function registeredReveal(
  protocol: PooClientProjection,
  masterCode: string,
  purpose: 'open' | 'accept',
): Promise<{ reveal: string; whitespaceRemoved: boolean }> {
  const registered = purpose === 'open'
    ? protocol.openingCommitment
    : protocol.acceptanceCommitment
  if (!registered) throw new Error('This OPP document has no registered commitment for that action.')

  let exactError: unknown = null
  try {
    const reveal = await deriveReveal(protocol, masterCode, purpose)
    if ((await commitment(protocol, purpose, reveal)) === registered) {
      return { reveal, whitespaceRemoved: false }
    }
  } catch (error) {
    exactError = error
  }

  const trimmedCode = masterCode.trim()
  if (trimmedCode !== masterCode) {
    try {
      const reveal = await deriveReveal(protocol, trimmedCode, purpose)
      if ((await commitment(protocol, purpose, reveal)) === registered) {
        return { reveal, whitespaceRemoved: true }
      }
    } catch {
      // Preserve the exact-value validation error below when neither form
      // matches. The trimmed attempt exists only for paste contamination.
    }
  }

  if (exactError instanceof Error) throw exactError
  throw new Error('This is not the OPP code registered for this document.')
}

async function apiRequest(input: RequestInfo, init?: RequestInit): Promise<PooClientProjection> {
  const response = await fetch(input, { ...init, cache: 'no-store', credentials: 'same-origin' })
  const result = await response.json() as PooApiResult
  if (!response.ok || !result.ok || !result.protocol) throw new Error(result.error || `OPP request failed (${response.status})`)
  treeStore.mutate.setPooProtocol(result.protocol)
  return result.protocol
}

function once(key: string, work: () => Promise<PooClientProjection>): Promise<PooClientProjection> {
  const current = pending.get(key)
  if (current) return current
  const request = work().finally(() => pending.delete(key))
  pending.set(key, request)
  return request
}

async function post(protocol: PooClientProjection, body: Record<string, unknown>): Promise<PooClientProjection> {
  return apiRequest('/api/msgdesk/poo', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...body, messageId: protocol.messageId }),
  })
}

export async function prepareOppRegistrationEmail(
  protocol: PooClientProjection,
  masterCode: string,
  publicCodeKey: string,
): Promise<{ href: string; openingReveal: string; acceptanceReveal: string | null }> {
  const registration = await prepareOppRegistration(protocol, masterCode)
  const replyAddress = `${publicCodeKey.trim()}@msgdesk.com`
  if (!publicCodeKey.trim() || /[\r\n@]/.test(publicCodeKey)) {
    throw new Error('This OPP document has no valid email reply address')
  }
  return {
    href: `mailto:${replyAddress}?subject=${encodeURIComponent(registration.subject)}&body=${encodeURIComponent(registration.body)}`,
    openingReveal: registration.openingReveal,
    acceptanceReveal: registration.acceptanceReveal,
  }
}

function preparedRegistrationFromCommitments(
  protocol: PooClientProjection,
  openingCommitment: string,
  acceptanceCommitment: string | null,
): { subject: string; body: string; openingCommitment: string; acceptanceCommitment: string | null } {
  if (!protocol.allowedActions.prepareEmailReply || !protocol.challenge || !protocol.recipientId) {
    throw new Error('This OPP document is not waiting for an email registration')
  }
  const opening = openingCommitment.trim().toLowerCase()
  const acceptance = acceptanceCommitment?.trim().toLowerCase() ?? null
  if (!/^[a-f0-9]{64}$/.test(opening)) throw new Error('The offline opening commitment must be a 64-character SHA-256 hex value')
  if (protocol.poaEnabled && !acceptance) throw new Error('This Strong OPP requires an offline acceptance commitment')
  if (acceptance && !/^[a-f0-9]{64}$/.test(acceptance)) throw new Error('The offline acceptance commitment must be a 64-character SHA-256 hex value')
  const subject = `[OPP-REG ${protocol.challenge}] MsgDesk OPP ${protocol.protocolVersion === 23 ? 'v23' : '2.2'} commitment registration`
  const body = protocol.protocolVersion === 23
    ? [
        '-----BEGIN MSGDESK OPP v23 RECIPIENT-----',
        `document: ${protocol.messageUuid}`,
        `challenge: ${protocol.challenge}`,
        `offer: ${protocol.offerHash ?? ''}`,
        `sender: ${protocol.senderId}`,
        `recipient: ${protocol.recipientId}`,
        `C_open: ${opening}`,
        `C_accept: ${acceptance ?? 'disabled'}`,
        'confirm: I CONFIRM THESE COMMITMENTS FOR THIS OFFER',
        '-----END MSGDESK OPP v23 RECIPIENT-----',
        '',
        'This email registers commitments only. It does not open or accept the document.',
      ].join('\n')
    : [
        '-----BEGIN MSGDESK OPP 2.2-----',
        `document: ${protocol.messageUuid}`,
        `challenge: ${protocol.challenge}`,
        `C_open: ${opening}`,
        `C_accept: ${acceptance ?? 'disabled'}`,
        '-----END MSGDESK OPP 2.2-----',
        '',
        'This email registers commitments only. It does not open or accept the document.',
      ].join('\n')
  return { subject, body, openingCommitment: opening, acceptanceCommitment: acceptance }
}

export function prepareOppRegistrationEmailFromCommitments(
  protocol: PooClientProjection,
  openingCommitment: string,
  acceptanceCommitment: string,
  publicCodeKey: string,
): { href: string } {
  const registration = preparedRegistrationFromCommitments(protocol, openingCommitment, acceptanceCommitment)
  const replyAddress = `${publicCodeKey.trim()}@msgdesk.com`
  if (!publicCodeKey.trim() || /[\r\n@]/.test(publicCodeKey)) {
    throw new Error('This OPP document has no valid email reply address')
  }
  return {
    href: `mailto:${replyAddress}?subject=${encodeURIComponent(registration.subject)}&body=${encodeURIComponent(registration.body)}`,
  }
}

function senderAttestation(protocol: PooClientProjection): { subject: string; body: string } {
  if (!protocol.allowedActions.prepareSenderAttestation || !protocol.senderId || !protocol.recipientId || !protocol.offerHash) {
    throw new Error('This Strong OPP document is not waiting for sender attestation')
  }
  const senderChallenge = protocol.senderChallenge
  if (!senderChallenge) throw new Error('The Strong sender challenge is unavailable')
  return {
    subject: `[OPP-SENDER ${senderChallenge}] MsgDesk OPP v23 offer attestation`,
    body: [
      '-----BEGIN MSGDESK OPP v23 SENDER-----',
      `document: ${protocol.messageUuid}`,
      `challenge: ${senderChallenge}`,
      `offer: ${protocol.offerHash}`,
      `sender: ${protocol.senderId}`,
      `recipient: ${protocol.recipientId}`,
      'confirm: I CONFIRM THIS OFFER',
      '-----END MSGDESK OPP v23 SENDER-----',
      '',
      'This email attests the exact frozen offer and parties. It does not open or accept the document.',
    ].join('\n'),
  }
}

export function prepareOppSenderAttestationEmail(
  protocol: PooClientProjection,
  publicCodeKey: string,
): { href: string } {
  const prepared = senderAttestation(protocol)
  const replyAddress = `${publicCodeKey.trim()}@msgdesk.com`
  if (!publicCodeKey.trim() || /[\r\n@]/.test(publicCodeKey)) {
    throw new Error('This OPP document has no valid email reply address')
  }
  return {
    href: `mailto:${replyAddress}?subject=${encodeURIComponent(prepared.subject)}&body=${encodeURIComponent(prepared.body)}`,
  }
}

export async function sendOppSenderAttestationEmail(protocol: PooClientProjection): Promise<PooClientProjection> {
  return once(`${protocol.messageUuid}:sender-attestation-template`, () => post(protocol, {
    action: 'send-sender-attestation-email',
  }))
}

async function prepareOppRegistration(
  protocol: PooClientProjection,
  masterCode: string,
): Promise<{
  subject: string
  body: string
  openingCommitment: string
  acceptanceCommitment: string | null
  openingReveal: string
  acceptanceReveal: string | null
}> {
  if (!protocol.allowedActions.prepareEmailReply || !protocol.challenge || !protocol.recipientId) {
    throw new Error('This OPP document is not waiting for an email registration')
  }
  const registrationCode = normalizeOppRegistrationMasterCode(masterCode)
  const openingReveal = await deriveReveal(protocol, registrationCode, 'open')
  const acceptanceReveal = protocol.poaEnabled
    ? await deriveReveal(protocol, registrationCode, 'accept')
    : null
  const openingCommitment = await commitment(protocol, 'open', openingReveal)
  const acceptanceCommitment = acceptanceReveal
    ? await commitment(protocol, 'accept', acceptanceReveal)
    : null
  const prepared = preparedRegistrationFromCommitments(protocol, openingCommitment, acceptanceCommitment)
  return {
    subject: prepared.subject,
    body: prepared.body,
    openingCommitment,
    acceptanceCommitment,
    openingReveal,
    acceptanceReveal,
  }
}

export async function sendOppRegistrationEmail(
  protocol: PooClientProjection,
  masterCode: string,
): Promise<PooClientProjection> {
  const registration = await prepareOppRegistration(protocol, masterCode)
  return once(`${protocol.messageUuid}:registration-template:${registration.openingCommitment}`, () => post(protocol, {
    action: 'send-registration-email',
    openingCommitment: registration.openingCommitment,
    acceptanceCommitment: registration.acceptanceCommitment,
  }))
}

export async function sendOppRegistrationEmailFromCommitments(
  protocol: PooClientProjection,
  openingCommitment: string,
  acceptanceCommitment: string,
): Promise<PooClientProjection> {
  const registration = preparedRegistrationFromCommitments(protocol, openingCommitment, acceptanceCommitment)
  return once(`${protocol.messageUuid}:registration-template:${registration.openingCommitment}`, () => post(protocol, {
    action: 'send-registration-email',
    openingCommitment: registration.openingCommitment,
    acceptanceCommitment: registration.acceptanceCommitment,
  }))
}

export async function registerOppInternalCommitments(
  protocol: PooClientProjection,
  masterCode: string,
): Promise<PooClientProjection> {
  if (!protocol.allowedActions.registerInternalCommitments) {
    throw new Error('This OPP document is not waiting for internal commitment registration')
  }
  const registrationCode = normalizeOppRegistrationMasterCode(masterCode)
  const openingReveal = await deriveReveal(protocol, registrationCode, 'open')
  const acceptanceReveal = protocol.poaEnabled
    ? await deriveReveal(protocol, registrationCode, 'accept')
    : null
  const openingCommitment = await commitment(protocol, 'open', openingReveal)
  const acceptanceCommitment = acceptanceReveal
    ? await commitment(protocol, 'accept', acceptanceReveal)
    : null
  return once(`${protocol.messageUuid}:internal-registration:${openingCommitment}`, () => post(protocol, {
    action: 'register-internal',
    openingCommitment,
    acceptanceCommitment,
    clientTime: Math.floor(Date.now() / 1000),
  }))
}

export async function resolveOppRecipient(
  protocol: PooClientProjection,
  recipientLookup: string,
): Promise<OppRecipientResolution> {
  const response = await fetch('/api/msgdesk/poo', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'resolve-recipient', messageId: protocol.messageId, recipientLookup }),
    cache: 'no-store',
    credentials: 'same-origin',
  })
  const result = await response.json() as PooApiResult
  if (!response.ok || !result.ok || !result.resolution) {
    throw new Error(result.error || `Recipient resolution failed (${response.status})`)
  }
  return result.resolution
}

async function enrollmentRequest(
  protocol: PooClientProjection,
  action: 'start-recipient-enrollment' | 'recipient-enrollment-status',
  recipientLookup?: string,
): Promise<OppEnrollmentStatus> {
  const response = await fetch('/api/msgdesk/poo', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, messageId: protocol.messageId, recipientLookup }),
    cache: 'no-store',
    credentials: 'same-origin',
  })
  const result = await response.json() as PooApiResult
  if (!response.ok || !result.ok || !result.enrollment) {
    throw new Error(result.error || `Recipient enrollment request failed (${response.status})`)
  }
  return result.enrollment
}

export function startOppRecipientEnrollment(
  protocol: PooClientProjection,
  recipientLookup: string,
): Promise<OppEnrollmentStatus> {
  return enrollmentRequest(protocol, 'start-recipient-enrollment', recipientLookup)
}

export function loadOppRecipientEnrollment(protocol: PooClientProjection): Promise<OppEnrollmentStatus> {
  return enrollmentRequest(protocol, 'recipient-enrollment-status')
}

export async function openPoo(
  protocol: PooClientProjection,
  masterCode: string,
): Promise<{ protocol: PooClientProjection; whitespaceRemoved: boolean }> {
  const registered = await registeredReveal(protocol, masterCode, 'open')
  const result = await once(`${protocol.messageUuid}:open`, async () => post(protocol, {
    action: 'open',
    secret: registered.reveal,
    clientTime: Math.floor(Date.now() / 1000),
  }))
  return { protocol: result, whitespaceRemoved: registered.whitespaceRemoved }
}

function normalizedOfflineReveal(value: string): string {
  const reveal = value.trim()
  if (!/^[A-Za-z0-9_-]{43}$/.test(reveal)) throw new Error('The offline reveal must be a 32-byte base64url value')
  return reveal
}

export async function openPooWithReveal(protocol: PooClientProjection, reveal: string): Promise<PooClientProjection> {
  return once(`${protocol.messageUuid}:open`, () => post(protocol, {
    action: 'open',
    secret: normalizedOfflineReveal(reveal),
    clientTime: Math.floor(Date.now() / 1000),
  }))
}

export async function acceptPoa(
  protocol: PooClientProjection,
  masterCode: string,
): Promise<{ protocol: PooClientProjection; whitespaceRemoved: boolean }> {
  const registered = await registeredReveal(protocol, masterCode, 'accept')
  const result = await once(`${protocol.messageUuid}:accept`, async () => post(protocol, {
    action: 'accept',
    secret: registered.reveal,
    clientTime: Math.floor(Date.now() / 1000),
  }))
  return { protocol: result, whitespaceRemoved: registered.whitespaceRemoved }
}

export async function acceptPoaWithReveal(protocol: PooClientProjection, reveal: string): Promise<PooClientProjection> {
  return once(`${protocol.messageUuid}:accept`, () => post(protocol, {
    action: 'accept',
    secret: normalizedOfflineReveal(reveal),
    clientTime: Math.floor(Date.now() / 1000),
  }))
}

export async function loadUnlockedPooContent(protocol: PooClientProjection): Promise<PooClientProjection> {
  if (protocol.openingState !== 'released' || protocol.fullContent) return protocol
  return once(`${protocol.messageUuid}:load-content`, () => post(protocol, { action: 'load-content' }))
}

export async function submitOppDispute(protocol: PooClientProjection, reason: string): Promise<PooClientProjection> {
  return once(`${protocol.messageUuid}:dispute`, () => post(protocol, { action: 'dispute', reason }))
}

export async function savePooSetup(
  protocol: PooClientProjection,
  input: { body: string; preview: string; poaEnabled: boolean },
): Promise<PooClientProjection> {
  return once(`${protocol.messageUuid}:save-setup`, () => post(protocol, { action: 'save-setup', ...input }))
}

export async function activatePoo(
  protocol: PooClientProjection,
  input: { body: string; preview: string; recipientEmail: string; poaEnabled: boolean }
    | {
        body: string
        preview: string
        recipientLookup: string
        expectedRecipientId: string
        expectedRecipientControllerId: string
        evidenceProfileId: 'basic-internal' | 'strong-external'
        notifyRecipient: boolean
        poaEnabled: boolean
      },
): Promise<PooClientProjection> {
  return once(`${protocol.messageUuid}:activate`, () => post(protocol, { action: 'activate', ...input }))
}

export async function resetPoo(protocol: PooClientProjection): Promise<PooClientProjection> {
  return once(`${protocol.messageUuid}:reset`, () => post(protocol, { action: 'reset' }))
}

export function receiptDocument(protocol: PooClientProjection): string {
  const firstPage = [
    `# MsgDesk OPP ${protocol.protocolVersion === 23 ? '2.3' : '2.2'} evidence summary`,
    '',
    `Evidence profile: ${protocol.evidenceProfile.profile}`,
    `Sender authorization: ${protocol.evidenceProfile.senderAuthorization}`,
    `Recipient enrollment: ${protocol.evidenceProfile.recipientEnrollment}`,
    `This package records ${protocol.evidenceProfile.opening === 'opened' ? 'an opening' : 'no opening'}, `
      + `${protocol.evidenceProfile.acceptance === 'accepted' ? 'an explicit acceptance' : 'no explicit acceptance'}, `
      + `mail evidence "${protocol.evidenceProfile.recipientMail}", delivery/dispute "${protocol.evidenceProfile.deliveryAndDispute}", `
      + `and external anchor state "${protocol.evidenceProfile.externalAnchor}".`,
    '',
    `Document: ${protocol.messageUuid}`,
    `Content SHA-256: ${protocol.contentHash ?? 'not frozen'}`,
    `Offer SHA-256: ${protocol.offerHash ?? 'not frozen'}`,
    `Opening SHA-256: ${protocol.openingHash ?? 'not opened'}`,
    `Acceptance SHA-256: ${protocol.acceptanceHash ?? 'not accepted'}`,
    `Recipient copy: ${protocol.recipientCopyState}`,
    `Dispute: ${protocol.disputeState}`,
    `Anchor: ${protocol.anchorState}${protocol.anchorLookupKey ? ` (${protocol.anchorLookupKey})` : ''}`,
    '',
    'Transport submission is not described as delivery. Anchor acknowledgement is not described as finalization.',
  ].join('\n')
  return `${firstPage}\n\n## Cryptographic appendix\n\n${JSON.stringify({
    protocolVersion: protocol.protocolVersion,
    messageId: protocol.messageId,
    messageUuid: protocol.messageUuid,
    contentHash: protocol.contentHash,
    offerHash: protocol.offerHash,
    enrollmentHash: protocol.enrollmentHash,
    opening: {
      commitment: protocol.openingCommitment,
      hash: protocol.openingHash,
      serverTime: protocol.openingServerTime,
      clientTime: protocol.openingClientTime,
      signature: protocol.openingServerSignature,
    },
    acceptance: protocol.poaEnabled ? {
      commitment: protocol.acceptanceCommitment,
      hash: protocol.acceptanceHash,
      serverTime: protocol.acceptanceServerTime,
      clientTime: protocol.acceptanceClientTime,
      signature: protocol.acceptanceServerSignature,
    } : null,
    signingKeyId: protocol.signingKeyId,
    signingPublicKey: protocol.signingPublicKey,
    transparencyLog: protocol.transparencyEvents,
  }, null, 2)}\n`
}

export async function downloadOppPublicCertificate(protocol: PooClientProjection): Promise<void> {
  const response = await fetch(`/api/msgdesk/poo/certificate?messageId=${encodeURIComponent(protocol.messageId)}`, {
    cache: 'no-store',
  })
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { error?: string }
    throw new Error(body.error || `Public certificate failed (${response.status})`)
  }
  const url = URL.createObjectURL(await response.blob())
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `msgdesk-opp-${protocol.messageUuid}-public.md`
  anchor.click()
  URL.revokeObjectURL(url)
}

export async function downloadOppEvidencePackage(protocol: PooClientProjection): Promise<void> {
  const response = await fetch(`/api/msgdesk/poo/evidence?messageId=${encodeURIComponent(protocol.messageId)}`, {
    cache: 'no-store',
    credentials: 'same-origin',
  })
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { error?: string }
    throw new Error(body.error || `Evidence package failed (${response.status})`)
  }
  const url = URL.createObjectURL(await response.blob())
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `msgdesk-opp-${protocol.messageUuid}.md`
  anchor.click()
  URL.revokeObjectURL(url)
}
